import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, IconButton, Tooltip } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Row from './components/Row';
import { dbs } from './components/Row';
import {
  ThemeProvider,
  createTheme,
  useColorScheme,
} from '@mui/material/styles';
import { CookiesProvider, useCookies } from 'react-cookie';
import ETLChangesRow from './components/ETLChangesRow';
import { fetchSyncOperations, fetchSyncJobs, fetchDistinctUsers } from './components/api';

const STATUSES = ['FINISHED', 'FAILED', 'PROCESSING', 'MERGING'];

function CollapsibleTable() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [targetFilter, setTargetFilter] = useState('');
  const [loginFilter, setLoginFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDisconnected, setShowDisconnected] = useState(false);
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [distinctUsers, setDistinctUsers] = useState([]);
  const { mode, setMode } = useColorScheme();
  const [cookies, setCookies] = useCookies(['theme']);

  const buildSearchFilters = useCallback(() => {
    const filters = {};
    if (targetFilter) filters.syncTarget = targetFilter;
    if (statusFilter) filters.syncStatus = statusFilter;
    if (loginFilter) filters.login = loginFilter;
    if (startDateFilter)
      filters.syncStart = startDateFilter.startOf('day').toISOString();
    if (endDateFilter)
      filters.syncFinishTime = endDateFilter.endOf('day').toISOString();
    return filters;
  }, [targetFilter, statusFilter, loginFilter, startDateFilter, endDateFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSyncOperations({
        page: page + 1,
        pageCount: rowsPerPage,
        searchFilters: buildSearchFilters(),
      });
      setRows(data.results);
      setTotalCount(data.totalCount);
    } catch (error) {
      console.error('Error fetching sync operations:', error);
      setShowDisconnected(true);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, buildSearchFilters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    fetchDistinctUsers()
      .then((users) => setDistinctUsers([...users].sort()))
      .catch((err) => console.error('Error fetching distinct users:', err));
  }, []);

  const closePopup = () => {
    setShowDisconnected(false);
    window.location.reload();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTargetFilterChange = (event) => {
    setTargetFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleThemeChange = (newMode) => {
    setMode(newMode);
    setCookies('theme', newMode, { path: '/' });
  };

  const removeFilters = () => {
    setStartDateFilter(null);
    setEndDateFilter(null);
    setTargetFilter('');
    setStatusFilter('');
    setLoginFilter('');
    setPage(0);
  };

  const noFiltersApplied =
    targetFilter === '' &&
    statusFilter === '' &&
    loginFilter === '' &&
    startDateFilter === null &&
    endDateFilter === null;

  const targetOptions = useMemo(() => {
    const seen = new Set();
    return Object.entries(dbs).filter(([key, displayName]) => {
      if (seen.has(displayName)) return false;
      seen.add(displayName);
      return true;
    });
  }, []);

  const handleLoginFilterChange = (event) => {
    setLoginFilter(event.target.value);
    setPage(0);
  };

  if (!mode)
    if (cookies.theme) setMode(cookies.theme);
    else return null;

  return (
    <CookiesProvider>
      <TableContainer sx={{ height: window.innerHeight }} component={Paper}>
        {showDisconnected && (
          <div className="popup">
            <div className="popup-content">
              <h3>Error</h3>
              <p>
                There was an error fetching the sync operations. Are you
                connected to the internal network?
              </p>
              <button onClick={closePopup}>Refresh</button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="loader">
            <img src="/pepe-the-frog-pepe.gif" alt="Loading" />
            <br />
            Loading...
          </div>
        ) : (
          <>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Stack direction="row">
                      <Tooltip title="Refresh table">
                        <IconButton onClick={loadData}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={mode === 'dark' ? 'Light mode' : 'Dark mode'}
                      >
                        <IconButton
                          onClick={() =>
                            handleThemeChange(
                              mode === 'dark' ? 'light' : 'dark'
                            )
                          }
                        >
                          {mode === 'dark' ? (
                            <LightModeIcon />
                          ) : (
                            <DarkModeIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell align="right">
                    <Select
                      value={loginFilter}
                      onChange={handleLoginFilterChange}
                      displayEmpty
                      fullWidth
                      size="small"
                      inputProps={{ 'aria-label': 'User' }}
                    >
                      <MenuItem value="">
                        <em>All Users</em>
                      </MenuItem>
                      {distinctUsers.map((login) => (
                        <MenuItem key={login} value={login}>
                          {login}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <Select
                      value={targetFilter}
                      onChange={handleTargetFilterChange}
                      displayEmpty
                      fullWidth
                      inputProps={{ 'aria-label': 'Without label' }}
                    >
                      <MenuItem value="">
                        <em>All Targets</em>
                      </MenuItem>
                      {targetOptions.map(([key, displayName]) => (
                        <MenuItem key={key} value={key}>
                          {displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">Type</TableCell>
                  <TableCell align="right">Entity Count</TableCell>
                  <TableCell align="right">Metadata Count</TableCell>
                  <TableCell align="right">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start"
                        value={startDateFilter}
                        maxDate={dayjs()}
                        onChange={(newStartDate) => {
                          setStartDateFilter(newStartDate);
                          setPage(0);
                        }}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell align="right">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End"
                        value={endDateFilter}
                        minDate={startDateFilter}
                        maxDate={dayjs()}
                        onChange={(newEndDate) => {
                          setEndDateFilter(newEndDate);
                          setPage(0);
                        }}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell align="right">
                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      displayEmpty
                      fullWidth
                      inputProps={{ 'aria-label': 'Without label' }}
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      {STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) =>
                  row.syncTarget === 'V2' ? (
                    <ETLChangesRow
                      key={row.syncBatchId}
                      row={row}
                      date={row.syncStart?.substring(0, 10)}
                      theme={mode}
                    />
                  ) : (
                    <Row
                      key={row.syncBatchId}
                      row={row}
                      fetchJobIds={fetchSyncJobs}
                      theme={mode}
                    />
                  )
                )}
              </TableBody>
            </Table>
            <Stack justifyContent="space-between" direction="row">
              <Button
                onClick={removeFilters}
                variant="outlined"
                color="primary"
                disabled={noFiltersApplied}
              >
                Remove filters
              </Button>
              <TablePagination
                align="right"
                rowsPerPageOptions={[10, 50, 100, 300]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Stack>
          </>
        )}
      </TableContainer>
    </CookiesProvider>
  );
}

export default function App() {
  const theme = createTheme({
    colorSchemes: {
      dark: true,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CollapsibleTable />
    </ThemeProvider>
  );
}
